import { Cart, CartItem, Location, Order, Page, PickupOptions, ResellerProduct, Tour } from "@/types"
import { formatProducts } from "@/utils/dataLayer"
import forEach from "lodash/forEach"
import includes from "lodash/includes"

export default function useDataLayer() {


    const getImpressions = function (): string[] {
        return window.Impressions ?? [];
    }


    const setImpressions = function (impressions: string[]) {
        window.Impressions = impressions;
    }

    const push = function (data: any) {
        const _ = window as Window
        window.dataLayer.push(data)
    }


    const registerPurchase = function (order: Order, cart?: Cart) {
        // Client-side dedup: only fire once per confirmation number, regardless of
        // whether the session-stashed cart survived the gateway redirect.
        const dedupKey = `purchase_tracked_${order.confirmation}`;
        try {
            if (localStorage.getItem(dedupKey)) return;
            localStorage.setItem(dedupKey, '1');
        } catch (e) {
            // localStorage unavailable (private mode, etc.) — fall through and fire anyway
        }

        // Clear  ecommerce object
        push({
            ecommerce: null
        });

        push({
            event: 'purchase',
            ecommerce: {
                transaction_id: order.confirmation,
                value: order.total,
                currency: 'USD',
                items: cart ? formatProducts(cart.items) : []
            },
            user_data: {
                email: order.customer_data?.email ?? '',
                phone: order.customer_data?.phone ?? '',
                first_name: order.customer_data?.first_name ?? '',
                last_name: order.customer_data?.last_name ?? '',
                zip: order.customer_data?.zip ?? '',
            }
        });
    }


    const registerTourVisit = function (tour: Tour, location?: Location, reseller_product?: ResellerProduct) {
        push({
            ecommerce: null
        })


        push({
            event: 'view_item',
            ecommerce: {
                currency: 'USD',
                value: reseller_product?.price ?? tour.price,
                items: [{
                    item_name: reseller_product?.name ?? tour.name,
                    item_id: reseller_product?.id ?? tour.resmark_id,
                    price: reseller_product?.price ?? tour.price,
                    // TODO: Update the APIs to return a tour categories string property should be returned to the datalayer
                    // item_category: < here >  

                }]
            }
        })
    }



    const registerAddToCart = function (tour: Tour, participants: { [key: string]: number }, pickupOption: PickupOptions) {
        const products: any[] = [];


        forEach(participants, (participantCount, tier) => {
            products.push({
                name: tour.name,
                id: tour.resmark_id,
                price: pickupOption.pickupDetails[0]?.price[tier],
                // TODO: Update the APIs to return a tour categories string property should be returned to the datalayer
                // category: < here >
                variant: tier,
                quantity: participantCount,
            })
        });

        // Google Analytics 4
        push({ ecommerce: null }); // Clear the previous ecommerce object.


        push({
            event: "add_to_cart",
            ecommerce: {
                items: products,
            },
        });
    }


    const registerRemoveFromCart = function (item: CartItem) {
        const products: any[] = [];


        forEach(item.participantList, (participant, key) => {
            products.push({
                name: item.name,
                price: participant.price,
                variant: participant.tier.label,
                quantity: 1
            })
        })


        // Google Analytics 4
        push({ ecommerce: null });  // Clear the previous ecommerce object.
        push({
            event: "remove_from_cart",
            ecommerce: {
                items: products
            }
        });
    }


    const registerProductClick = function (product: Tour, position: any, location?: Location) {
        push({ ecommerce: null });
        push({
            event: "select_item",
            ecommerce: {
                items: [{
                    item_id: product.resmark_id,
                    item_name: product.name,
                    // TODO: Update the APIs to return a tour categories string property should be returned to the datalayer
                    // item_category: < here >  
                    price: product.price,
                    location_id: location?.id
                }]
            }
        });

    }



    const registerProductImpression = function (product: Tour, position: any, location?: Location) {
        const impressions = getImpressions();


        if (!includes(impressions, product.resmark_id)) {
            setImpressions([...impressions, product.resmark_id]);




            push({ ecommerce: null })

            push({
                event: "view_item_list",
                ecommerce: {
                    items: [{
                        item_id: product.resmark_id,
                        item_name: product.name,
                        // TODO: Update the APIs to return a tour categories string property should be returned to the datalayer
                        // item_category: < here > 
                        location_id: location?.id,
                        price: product.price
                    }]
                }
            });


        }
    }


    const registerStartCheckout = function (cart: Cart) {
        // Google Analytic 4
        push({ ecommerce: null }) // Clear the previous ecommerce object.

        push({
            event: "begin_checkout",
            ecommerce: {
                currency: 'USD',
                value: cart.total,
                items: formatProducts(cart.items)
            }
        })
    }


    const registerPageVisit = function (page: Page) {
        push({
            page: {
                page_id: page.seo_title ?? page.title
            }
        })
    }



    return {
        push, registerPurchase,
        registerTourVisit, registerAddToCart,
        registerRemoveFromCart, registerProductClick,
        registerProductImpression, registerStartCheckout,
        registerPageVisit
    }
}